import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Box, Typography, Checkbox, FormControlLabel, Alert, Chip, IconButton,
  Tooltip, CircularProgress, ToggleButtonGroup, ToggleButton,
  Collapse, InputAdornment, Popover, Divider, Table, TableBody, TableCell,
  TableHead, TableRow,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Edit as EditIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { fetchVenues } from '../store/slices/venuesSlice';

// Format a "2026-06-10T00:00" style local-datetime string as the
// human-readable "Wed, Jun 10" the KDM window readout uses. Returns
// "—" for falsy / unparseable input so the operator sees that the
// derived value isn't available yet (typically because screening
// dates haven't been entered).
function fmtWindowDate(local) {
  if (!local) return '—';
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

// Pure: filter `candidates` by free-text `search`, then group the survivors
// by chain. Exported so __tests__/distribute_dialog_grouping.test.js can
// pin the grouping/filter behavior independently of the React tree.
//
// `candidates` is the flat [{venue_name, chain, city, state, screen_name,
// server_make, server_model, tech_contact_email, ...}] array the dialog
// already builds. The returned shape is
// [{ chain: '<name>' | 'Independent', items: [<candidate>, ...] }] in
// alphabetical chain order, "Independent" (no chain) always last so the
// catalog reads like the Venues page.
export function groupCandidatesByChain(candidates, search) {
  const term = (search || '').trim().toLowerCase();
  const filtered = term
    ? (candidates || []).filter(c => {
      const hay = [c.venue_name, c.chain, c.city, c.state, c.screen_name,
        c.server_make, c.server_model, c.tech_contact_email]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(term);
    })
    : (candidates || []);
  const groups = new Map();
  for (const c of filtered) {
    const key = c.chain && c.chain.trim() ? c.chain.trim() : 'Independent';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(c);
  }
  // Alpha-sort the chain names; Independent always last so it doesn't
  // sandwich into the I's of the alphabet.
  const sorted = Array.from(groups.entries()).sort(([a], [b]) => {
    if (a === 'Independent') return 1;
    if (b === 'Independent') return -1;
    return a.localeCompare(b);
  });
  return sorted.map(([chain, items]) => ({ chain, items }));
}

// One-shot DCP distribution: pick venues/screens, set validity window,
// generate KDMs + create delivery shares + send a single email per venue
// with the .kdm.xml attached and the download link inline.
//
// Strictly additive — never touches existing non-revoked KDMs or Shares
// unless force_replace is checked. Pre-flight errors return as 409 so
// the operator can adjust before any side effects fire.
export default function DCPDistributeDialog({ open, onClose, dcp, onSuccess }) {
  const dispatch = useDispatch();
  const { venues } = useSelector((state) => state.venues);

  // v10.17.15: primary inputs are screening dates; the validity window
  // is derived from them via the shared policy. Override expander is
  // kept for festival residencies / multi-week runs.
  const [screeningFirst, setScreeningFirst] = useState('');
  const [screeningLast, setScreeningLast] = useState('');
  const [overrideWindow, setOverrideWindow] = useState(false);
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [expiresDays, setExpiresDays] = useState(14);
  const [ccEmail, setCcEmail] = useState('');
  const [operatorMessage, setOperatorMessage] = useState('');
  const [forceReplace, setForceReplace] = useState(false);
  const [picks, setPicks] = useState({});  // screen_id -> { venue_id, recipient_email, recipient_name, cc_email }
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [results, setResults] = useState(null);
  // Physical-media delivery: rsync the DCP package onto a USB drive on a
  // registered physical_delivery_node. No emails to recipients — only a
  // summary email to the operator. KDMs still issued for projector ingest.
  const [deliveryMode, setDeliveryMode] = useState('digital'); // 'digital' | 'physical'
  const [physicalNodes, setPhysicalNodes] = useState([]);
  const [physicalNodeId, setPhysicalNodeId] = useState(null);
  const [physicalNodeStatus, setPhysicalNodeStatus] = useState(null);
  const [physicalExtract, setPhysicalExtract] = useState(true);
  // For encrypted DCPs only. TRUE (default): KDM goes on the drive
  // alongside the DCP. FALSE: ship the DCP only — the operator delivers
  // the KDM through a separate channel (email/courier).
  const [physicalIncludeKdm, setPhysicalIncludeKdm] = useState(true);
  const [operatorNotifyEmail, setOperatorNotifyEmail] = useState('');
  const userInfo = useSelector((state) => state.auth?.userInfo);

  useEffect(() => {
    if (!open) return;
    dispatch(fetchVenues());
    setResults(null);
    setErrorMsg('');
    setExpiresDays(14);
    setForceReplace(false);
    setPicks({});
    setOperatorMessage('');
    setCcEmail('');
    setDeliveryMode('digital');
    setPhysicalExtract(true);
    setPhysicalIncludeKdm(true);
    setOperatorNotifyEmail(userInfo?.email || '');
    setVenueSearch('');
    setCollapsedChains(new Set());
    setEditPopover(null);
    setAdvancedOpen(false);

    // v10.17.15: pre-fill the SCREENING DATE from the most recent venue
    // request — the validity window is derived from it client-side and
    // re-derived server-side by services/kdm_window_policy. The operator
    // never types valid_from / valid_to directly on the happy path,
    // closing off the "06-vs-05" date-picker typo class.
    const pad = (n) => String(n).padStart(2, '0');
    const dateOnly = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    setOverrideWindow(false);
    (async () => {
      let firstScreening = null;
      let lastScreening = null;
      try {
        // NB: the kdm_request router mounts WITHOUT the /api/ prefix
        // (mixed-auth: public /request/{token} sits alongside
        // operator-only /request/links). Calling /api/request/links
        // 404s — bug I introduced in v10.17.15 by adding /api/
        // assuming standard prefix; reverted here in v10.17.22 and
        // pinned in __tests__/distribute_dialog_request_url.test.jsx.
        const { data } = await api.get('/request/links');
        const submitted = (data || [])
          .filter(t => t.first_screening_at && t.last_screening_at)
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        if (submitted.length > 0) {
          firstScreening = new Date(submitted[0].first_screening_at);
          lastScreening = new Date(submitted[0].last_screening_at);
        }
      } catch (_e) { /* fall through to defaults */ }
      const now = new Date();
      const fromDate = firstScreening || new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      const toDate = lastScreening || fromDate;
      setScreeningFirst(dateOnly(fromDate));
      setScreeningLast(dateOnly(toDate));
    })();
  }, [open, dispatch, userInfo?.email]);

  // Derive the validity window from the screening dates. Anchored to
  // local 00:00 / 23:59 so a "2 days after" is a real calendar day.
  // Mirrors services/kdm_window_policy.compute_window on the backend.
  const DAYS_BEFORE = 14;
  const DAYS_AFTER = 2;
  const derivedWindow = useMemo(() => {
    if (!screeningFirst) return { validFrom: '', validTo: '' };
    const last = screeningLast || screeningFirst;
    const f = new Date(`${screeningFirst}T00:00:00`);
    const l = new Date(`${last}T23:59:00`);
    if (Number.isNaN(f.getTime()) || Number.isNaN(l.getTime())) {
      return { validFrom: '', validTo: '' };
    }
    const vfd = new Date(f); vfd.setDate(vfd.getDate() - DAYS_BEFORE);
    const vtd = new Date(l); vtd.setDate(vtd.getDate() + DAYS_AFTER);
    const pad = (n) => String(n).padStart(2, '0');
    const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    return { validFrom: fmt(vfd), validTo: fmt(vtd) };
  }, [screeningFirst, screeningLast]);

  useEffect(() => {
    if (!overrideWindow && derivedWindow.validFrom && derivedWindow.validTo) {
      setValidFrom(derivedWindow.validFrom);
      setValidTo(derivedWindow.validTo);
    }
  }, [derivedWindow.validFrom, derivedWindow.validTo, overrideWindow]);

  // Lazy-load registered physical delivery nodes once when the operator
  // flips to physical mode. Silent 401/403 = non-operator users; the
  // mode toggle won't render in that case anyway.
  useEffect(() => {
    if (!open || deliveryMode !== 'physical' || physicalNodes.length > 0) return;
    (async () => {
      try {
        const r = await api.get('/api/physical-delivery/nodes');
        const list = (r.data || []).filter((n) => n.is_active);
        setPhysicalNodes(list);
        if (list.length && !physicalNodeId) setPhysicalNodeId(list[0].id);
      } catch (_e) { setPhysicalNodes([]); }
    })();
  }, [open, deliveryMode, physicalNodes.length, physicalNodeId]);

  useEffect(() => {
    if (deliveryMode !== 'physical' || !physicalNodeId) { setPhysicalNodeStatus(null); return; }
    (async () => {
      try {
        const r = await api.get(`/api/physical-delivery/nodes/${physicalNodeId}/status`);
        setPhysicalNodeStatus(r.data);
      } catch (_e) { setPhysicalNodeStatus(null); }
    })();
  }, [deliveryMode, physicalNodeId]);

  // Flatten venues -> screens. Every active screen on every active venue
  // gets a row; screens that can't actually receive THIS DCP are
  // surfaced disabled with an inline reason instead of dropped silently
  // — operator can see at a glance which venues need cert/format work.
  const [showOnlyEligible, setShowOnlyEligible] = useState(false);
  const candidates = useMemo(() => {
    const rows = [];
    (venues || []).forEach(v => {
      // Backend already filters is_active + non-deleted for `venues`,
      // but be defensive in case a stale cache survives a long session.
      if (v.is_active === false || v.deleted_at) return;
      (v.screens || []).forEach(s => {
        if (s.is_active === false || s.deleted_at) return;
        // Compute eligibility + the operator-readable reason a screen
        // can't ingest THIS DCP. Screens that COULD ingest stay enabled;
        // disabled rows still render so operator sees what needs work.
        const isMp4Only = (s.playback_type || 'dcp') === 'mp4';
        const isEncrypted = !!dcp?.encrypt;
        const hasCert = !!s.certificate_filename;
        // v11.0.0 — screens flagged cert_not_required (Flappers,
        // Filmmaker Master Backup, etc.) are exempt from the
        // "no cert on file" blocker; encrypted DCPs would still
        // need a KDM, but those screens don't ingest encrypted
        // content so the operator shouldn't be sending them anyway.
        const certExempt = !!s.cert_not_required;
        let blockedReason = null;
        if (isMp4Only) blockedReason = 'MP4-only — DCPs not deliverable';
        else if (isEncrypted && !hasCert && !certExempt) blockedReason = 'No cert on file';
        else if (isEncrypted && certExempt) blockedReason = 'Unencrypted-only venue — encrypted DCPs not deliverable';
        rows.push({
          screen_id: s.id,
          venue_id: v.id,
          venue_name: v.name,
          chain: v.chain || '',
          city: v.city || '',
          state: v.state || '',
          screen_name: s.name,
          tech_contact_email: v.tech_contact_email || '',
          tech_contact_name: v.tech_contact_name || '',
          has_cert: hasCert,
          server_make: s.server_make || '',
          server_model: s.server_model || '',
          blocked_reason: blockedReason,
        });
      });
    });
    rows.sort((a, b) => (a.chain || a.venue_name).localeCompare(b.chain || b.venue_name) || a.venue_name.localeCompare(b.venue_name));
    return rows;
  }, [venues, dcp]);

  // Filtered view based on the "show only eligible" pill.
  const visibleCandidates = useMemo(
    () => showOnlyEligible ? candidates.filter(c => !c.blocked_reason) : candidates,
    [candidates, showOnlyEligible],
  );
  const blockedCount = candidates.filter(c => c.blocked_reason).length;

  // v10.21.55 venue picker rebuild: search + chain grouping + popover
  // edit. State shape:
  //   venueSearch       — free-text filter against name/chain/city/etc
  //   collapsedChains   — Set of chain names the operator collapsed
  //   editPopover       — { anchorEl, screen_id } | null  (open state)
  const [venueSearch, setVenueSearch] = useState('');
  const [collapsedChains, setCollapsedChains] = useState(() => new Set());
  const [editPopover, setEditPopover] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const groupedCandidates = useMemo(
    () => groupCandidatesByChain(visibleCandidates, venueSearch),
    [visibleCandidates, venueSearch],
  );
  const totalGrouped = useMemo(
    () => groupedCandidates.reduce((n, g) => n + g.items.length, 0),
    [groupedCandidates],
  );
  // When `errorMsg` becomes non-empty, scroll the top alert into view —
  // operators on tall screens were missing 409/pre-flight errors that
  // rendered above the venue list while they were scrolled to the
  // Distribute button at the bottom.
  const errorAlertRef = useRef(null);
  useEffect(() => {
    if (errorMsg && errorAlertRef.current) {
      errorAlertRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [errorMsg]);

  const togglePick = async (row) => {
    setPicks(prev => {
      if (prev[row.screen_id]) {
        const next = { ...prev };
        delete next[row.screen_id];
        return next;
      }
      // Optimistic insert with the legacy tech_contact email; the
      // resolver call below replaces it with the role-routed defaults.
      return {
        ...prev,
        [row.screen_id]: {
          venue_id: row.venue_id,
          recipient_email: row.tech_contact_email || '',
          recipient_name: row.tech_contact_name || row.venue_name,
          cc_email: '',
          loading: true,
        },
      };
    });
    // Pull role-routed defaults for this venue. recipient_email is
    // capped at 255 chars in the schema, so we put the primary "To" in
    // recipient_email and route every additional contact (rest of To +
    // all CC roles) into cc_email which has 500 chars. Email semantics
    // still hold: everyone gets the message; the primary tech contact
    // gets the "To" line, others see themselves on CC.
    try {
      const { data } = await api.get(`/api/venues/${row.venue_id}/recipients?email_type=distribution`);
      const toList = data.to || [];
      const ccList = data.cc || [];
      const primary = toList[0] || prev_recipient_email_fallback(row);
      const allCc = [...toList.slice(1), ...ccList];
      // De-dupe (case-insensitive).
      const seen = new Set([primary.toLowerCase()]);
      const dedupedCc = allCc.filter(e => {
        const k = e.toLowerCase();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
      setPicks(prev => {
        if (!prev[row.screen_id]) return prev;
        return {
          ...prev,
          [row.screen_id]: {
            ...prev[row.screen_id],
            recipient_email: primary,
            cc_email: dedupedCc.join(', '),
            loading: false,
          },
        };
      });
    } catch (_) {
      setPicks(prev => prev[row.screen_id] ? { ...prev, [row.screen_id]: { ...prev[row.screen_id], loading: false } } : prev);
    }
  };

  function prev_recipient_email_fallback(row) {
    return row.tech_contact_email || '';
  }

  const updatePick = (screen_id, field, value) => {
    setPicks(prev => ({ ...prev, [screen_id]: { ...prev[screen_id], [field]: value } }));
  };

  const submit = async () => {
    setErrorMsg('');
    setResults(null);
    const targets = Object.entries(picks).map(([screen_id, p]) => ({
      screen_id: Number(screen_id),
      venue_id: p.venue_id,
      recipient_email: p.recipient_email,
      recipient_name: p.recipient_name,
      cc_email: p.cc_email || ccEmail || null,
    }));
    if (targets.length === 0) {
      setErrorMsg('Pick at least one venue/screen.');
      return;
    }
    if (deliveryMode === 'digital' && targets.some(t => !t.recipient_email)) {
      setErrorMsg('Every selected screen must have a recipient email — fill in the missing ones.');
      return;
    }
    if (deliveryMode === 'physical') {
      if (!physicalNodeId) {
        setErrorMsg('Pick a physical delivery node.');
        return;
      }
      if (!physicalNodeStatus?.mounted) {
        setErrorMsg('Drive is not mounted on the selected node. Mount it from Operator · Physical Drives first.');
        return;
      }
    }
    if (!screeningFirst) {
      setErrorMsg('Set the screening date so the KDM window can be derived.');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        targets,
        screening_first: new Date(`${screeningFirst}T00:00:00`).toISOString(),
        screening_last: new Date(`${screeningLast || screeningFirst}T23:59:00`).toISOString(),
        expires_days: Number(expiresDays) || 14,
        operator_message: operatorMessage || null,
        force_replace: forceReplace,
        delivery_mode: deliveryMode,
        ...(overrideWindow ? {
          valid_from: new Date(validFrom).toISOString(),
          valid_to: new Date(validTo).toISOString(),
          force_window: true,
        } : {}),
        ...(deliveryMode === 'physical' ? {
          physical_node_id: physicalNodeId,
          physical_extract: physicalExtract,
          physical_include_kdm: physicalIncludeKdm,
          operator_notify_email: operatorNotifyEmail || null,
        } : {}),
      };
      const { data } = await api.post(`/api/dcp-jobs/${dcp.id}/distribute`, payload);
      setResults(data.results || []);
      // Let the parent refresh its packages / KDM lists while the
      // results table stays visible. Optional — DCPLibrary refreshes
      // on close instead, so callers can use either pattern.
      if (typeof onSuccess === 'function') onSuccess(data.results || []);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && detail.errors && Array.isArray(detail.errors)) {
        setErrorMsg(detail.errors.join('\n'));
      } else {
        setErrorMsg(typeof detail === 'string' ? detail : (e.message || 'Distribution failed'));
      }
    } finally {
      setBusy(false);
    }
  };

  const pickedCount = Object.keys(picks).length;

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>
        Distribute · {dcp?.name} <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>#{dcp?.id}</Typography>
        {dcp?.output_path && (
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontFamily: 'monospace', fontSize: '0.7rem' }}>
            {dcp.output_path.split('/').pop()}
          </Typography>
        )}
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
          {dcp?.encrypt
            ? 'Generates KDMs + creates delivery links + sends one email per venue with the KDM attached.'
            : 'Creates delivery links + sends one email per recipient (unencrypted — no KDM needed).'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {results ? (
          <Box>
            {(() => {
              const physRows = results.filter(r => r.delivery_mode === 'physical');
              const physOk = physRows.filter(r => r.ok);
              if (physOk.length === 0) return null;
              return (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>Physical delivery requested</strong> for {physOk.length} {physOk.length === 1 ? 'venue' : 'venues'}.
                  An HHMG operator will connect a drive and begin staging.
                  You'll receive an email when each drive is ready to be shipped.
                </Alert>
              );
            })()}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Results</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Venue</TableCell>
                  <TableCell>Screen</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>{r.venue}</TableCell>
                    <TableCell>{r.screen}</TableCell>
                    <TableCell>
                      {r.ok ? (
                        r.delivery_mode === 'physical' ? (
                          <Chip size="small" color="info" label={
                            (r.kdm_id ? `KDM #${r.kdm_id} · ` : '') + `Staging #${r.physical_delivery_id}`
                          } />
                        ) : (
                          <Chip size="small" color="success" label={dcp?.encrypt ? `KDM #${r.kdm_id} · Share #${r.share_id}` : `Share #${r.share_id}`} />
                        )
                      ) : (
                        <Tooltip title={r.error || ''}><Chip size="small" color="error" label="Failed" /></Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <>
            {errorMsg && <Alert ref={errorAlertRef} severity="error" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>{errorMsg}</Alert>}

            {/* Delivery mode toggle — digital (default) vs physical-media. */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Delivery mode</Typography>
              <ToggleButtonGroup
                value={deliveryMode}
                exclusive
                onChange={(_, v) => { if (v) setDeliveryMode(v); }}
                size="small"
              >
                <ToggleButton value="digital">Email recipient</ToggleButton>
                <ToggleButton value="physical">Stage to physical drive</ToggleButton>
              </ToggleButtonGroup>
              {deliveryMode === 'physical' && (
                <Box sx={{ mt: 1.5, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  {!dcp?.encrypt && (
                    <Alert severity="success" icon={false} sx={{ py: 0.5, mb: 1 }}>
                      <strong>Unencrypted DCP</strong> — no KDM will be issued. Screen
                      selection determines the shipping address only.
                    </Alert>
                  )}
                  {physicalNodes.length === 0 && (
                    <Alert severity="warning" sx={{ py: 0.5 }}>
                      No active physical delivery nodes registered.
                    </Alert>
                  )}
                  {physicalNodes.length > 0 && (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Drive
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        {physicalNodes.map((n) => (
                          <Chip key={n.id} label={n.name}
                                color={n.id === physicalNodeId ? 'primary' : 'default'}
                                variant={n.id === physicalNodeId ? 'filled' : 'outlined'}
                                onClick={() => setPhysicalNodeId(n.id)} />
                        ))}
                      </Box>
                      {physicalNodeStatus ? (
                        physicalNodeStatus.mounted ? (
                          <Typography variant="caption" color="text.secondary">
                            {physicalNodeStatus.fs} ·{' '}
                            {((physicalNodeStatus.free_bytes || 0) / (1024 ** 3)).toFixed(1)} GB free of{' '}
                            {((physicalNodeStatus.size_bytes || 0) / (1024 ** 3)).toFixed(1)} GB
                          </Typography>
                        ) : (
                          <Alert severity="warning" sx={{ py: 0 }}>
                            Drive not mounted. Mount it from Operator · Physical Drives first.
                          </Alert>
                        )
                      ) : (
                        <Typography variant="caption" color="text.secondary">Checking drive…</Typography>
                      )}
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mt: 1.5 }}>
                        <FormControlLabel
                          control={<Checkbox size="small" checked={physicalExtract}
                                             onChange={(e) => setPhysicalExtract(e.target.checked)} />}
                          label={<Typography variant="caption">Extract on drive (DCP folder ready for projector ingest)</Typography>}
                        />
                        {dcp?.encrypt && (
                          <FormControlLabel
                            control={<Checkbox size="small" checked={physicalIncludeKdm}
                                               onChange={(e) => setPhysicalIncludeKdm(e.target.checked)} />}
                            label={<Typography variant="caption">
                              Include KDM on drive (uncheck to ship DCP only — KDM via email/separate)
                            </Typography>}
                          />
                        )}
                        <TextField size="small" label="Operator notification email"
                                   value={operatorNotifyEmail}
                                   onChange={(e) => setOperatorNotifyEmail(e.target.value)}
                                   placeholder={userInfo?.email || 'you@example.com'}
                                   helperText="Single summary email; recipients receive nothing." />
                      </Box>
                    </>
                  )}
                </Box>
              )}
            </Box>

            {/* When is the show — the canonical input the KDM window
                derives from. v10.21.55 caption replaces the raw
                "valid_from/valid_to" jargon with plain dates. */}
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>When is the show?</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              We unlock the KDM 14 days before the first screening and lock it 2 days after the last.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: deliveryMode === 'physical' ? '1fr 1fr' : '1fr 1fr 1fr' }, gap: 1.5, mb: 1 }}>
              <TextField type="date" size="small" label="First screening"
                value={screeningFirst} onChange={(e) => setScreeningFirst(e.target.value)}
                InputLabelProps={{ shrink: true }} />
              <TextField type="date" size="small" label="Last screening"
                value={screeningLast} onChange={(e) => setScreeningLast(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Same as first for one-night shows" />
              {deliveryMode === 'digital' && (
                <TextField type="number" size="small" label="Link expires (days)"
                  value={expiresDays} onChange={(e) => setExpiresDays(e.target.value)}
                  helperText="How long the download link stays live." />
              )}
            </Box>
            {/* KDM window readout — derived from the screening dates above,
                shown in plain language. Override hidden behind Advanced. */}
            <Box sx={{
              p: 1, mb: 1.5, borderRadius: 1,
              border: 1, borderColor: 'divider', bgcolor: 'action.hover',
              display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
            }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                KDM window:
              </Typography>
              <Typography variant="caption">
                Unlocks <strong>{fmtWindowDate(derivedWindow.validFrom)}</strong> · Locks <strong>{fmtWindowDate(derivedWindow.validTo)}</strong>
              </Typography>
            </Box>

            {/* Advanced options — collapsed by default. The 99%-case
                operator never opens this; the override + force_replace +
                CC-everyone + cover-note all live here. */}
            <Box sx={{ mb: 2 }}>
              <Button
                size="small"
                onClick={() => setAdvancedOpen(v => !v)}
                startIcon={advancedOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                sx={{ textTransform: 'none', color: 'text.secondary' }}
              >
                Advanced options
              </Button>
              <Collapse in={advancedOpen} unmountOnExit>
                <Box sx={{ pt: 1, pl: 1, borderLeft: 2, borderColor: 'divider', ml: 1 }}>
                  <FormControlLabel
                    control={<Checkbox size="small" checked={overrideWindow} onChange={(e) => setOverrideWindow(e.target.checked)} />}
                    label={<Typography variant="body2">Override KDM window manually</Typography>}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mb: 1 }}>
                    Use only for festival residencies / multi-week runs where the policy default is too tight.
                  </Typography>
                  {overrideWindow && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 1.5, ml: 4 }}>
                      <TextField type="datetime-local" size="small" label="Unlock at"
                        value={validFrom} onChange={(e) => setValidFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }} />
                      <TextField type="datetime-local" size="small" label="Lock at"
                        value={validTo} onChange={(e) => setValidTo(e.target.value)}
                        InputLabelProps={{ shrink: true }} />
                    </Box>
                  )}
                  {deliveryMode === 'digital' && (
                    <TextField
                      size="small" label="CC every venue's email" fullWidth
                      value={ccEmail}
                      onChange={(e) => setCcEmail(e.target.value)}
                      placeholder="ops@example.com"
                      helperText="Added to CC on every outgoing email — separate from each venue's own CC list."
                      sx={{ mb: 1.5 }}
                    />
                  )}
                  <FormControlLabel
                    control={<Checkbox checked={forceReplace} onChange={(e) => setForceReplace(e.target.checked)} />}
                    label={<Typography variant="body2">Force replace existing KDM / share</Typography>}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mb: 1.5 }}>
                    Re-issues a fresh KDM and creates a new download link even when the venue already has an active one. Old KDM stays valid; old share is revoked.
                  </Typography>
                  {deliveryMode === 'digital' && (
                    <TextField size="small" label="Note for the email body" fullWidth multiline minRows={2}
                      value={operatorMessage} onChange={(e) => setOperatorMessage(e.target.value)}
                      helperText="Short text appended to every email (e.g. 'Please confirm receipt — thanks!')."
                    />
                  )}
                </Box>
              </Collapse>
            </Box>

            {/* v10.21.55: search + chain grouping. The flat alphabetical
                wall of screens was unusable past ~15 venues — every pick
                inflated the row to 3 lines tall with inline To/CC editing.
                Search filters across name/chain/city/server; rows group
                under collapsible chain headers; picked rows show recipient
                as a single chip with an edit popover. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2">Pick venues / screens</Typography>
              <Chip size="small" label={`${pickedCount} selected`}
                color={pickedCount > 0 ? 'primary' : 'default'}
                variant={pickedCount > 0 ? 'filled' : 'outlined'}
                sx={{ height: 20, fontSize: '0.65rem' }} />
              {blockedCount > 0 && (
                <Chip
                  size="small"
                  label={showOnlyEligible
                    ? `Showing eligible only (${blockedCount} hidden)`
                    : `${blockedCount} blocked — needs cert / wrong format`}
                  color={showOnlyEligible ? 'default' : 'warning'}
                  variant={showOnlyEligible ? 'outlined' : 'filled'}
                  onClick={() => setShowOnlyEligible(v => !v)}
                  clickable
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              )}
              <Box sx={{ flexGrow: 1 }} />
              <Tooltip title="Refresh venue list"><IconButton size="small" onClick={() => dispatch(fetchVenues())}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
            </Box>
            <TextField
              size="small"
              fullWidth
              placeholder="Search venue, chain, city, server (e.g. 'austin', 'gdc', 'galaxy')…"
              value={venueSearch}
              onChange={(e) => setVenueSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                endAdornment: venueSearch ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setVenueSearch('')}><ClearIcon fontSize="small" /></IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{ mb: 1 }}
            />
            {candidates.length === 0 ? (
              <Alert severity="warning">
                No active venue screens. Add a venue from the Venues page first.
              </Alert>
            ) : totalGrouped === 0 ? (
              <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
                No matches for &ldquo;{venueSearch}&rdquo;. Try a shorter term or clear the search.
              </Alert>
            ) : (
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                {groupedCandidates.map(({ chain, items }) => {
                  const collapsed = collapsedChains.has(chain);
                  const chainPicked = items.filter(c => picks[c.screen_id]).length;
                  return (
                    <Box key={chain}>
                      <Box
                        onClick={() => setCollapsedChains(prev => {
                          const next = new Set(prev);
                          if (next.has(chain)) next.delete(chain); else next.add(chain);
                          return next;
                        })}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1, p: 1,
                          cursor: 'pointer',
                          bgcolor: 'action.hover',
                          borderBottom: collapsed ? 0 : 1,
                          borderColor: 'divider',
                          '&:hover': { bgcolor: 'action.selected' },
                        }}
                      >
                        {collapsed ? <ChevronRightIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        <Typography variant="body2" fontWeight={600}>{chain}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {chainPicked} / {items.length} {items.length === 1 ? 'screen' : 'screens'}
                        </Typography>
                      </Box>
                      <Collapse in={!collapsed} unmountOnExit>
                        <Box>
                          {items.map((c, idx) => {
                            const picked = !!picks[c.screen_id];
                            const blocked = !!c.blocked_reason;
                            const pick = picked ? picks[c.screen_id] : null;
                            const recipChip = pick && (pick.loading
                              ? 'loading…'
                              : (pick.recipient_email || '(no recipient)'));
                            return (
                              <Box key={c.screen_id} sx={{
                                display: 'flex', alignItems: 'center', gap: 1,
                                px: 1, py: 0.75,
                                borderTop: idx === 0 ? 0 : 1,
                                borderColor: 'divider',
                                opacity: blocked ? 0.55 : 1,
                                bgcolor: picked ? 'action.selected' : 'transparent',
                              }}>
                                <Tooltip title={c.blocked_reason || ''} arrow placement="right">
                                  <span>
                                    <Checkbox
                                      size="small"
                                      checked={picked}
                                      disabled={blocked}
                                      onChange={() => togglePick(c)}
                                      sx={{ p: 0.5 }}
                                    />
                                  </span>
                                </Tooltip>
                                <Box sx={{ minWidth: 0, flex: '1 1 240px' }}>
                                  <Typography variant="body2" fontWeight={500} noWrap>{c.venue_name}</Typography>
                                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem' }} noWrap>
                                    {[c.city, c.state].filter(Boolean).join(', ') || ' '}
                                  </Typography>
                                </Box>
                                <Box sx={{ minWidth: 0, flex: '1 1 180px' }}>
                                  <Typography variant="body2" noWrap>{c.screen_name}</Typography>
                                  {(c.server_make || c.server_model) && (
                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.65rem' }} noWrap>
                                      {[c.server_make, c.server_model].filter(Boolean).join(' ')}
                                    </Typography>
                                  )}
                                </Box>
                                <Box sx={{ flex: '1 1 280px', minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {blocked ? (
                                    <>
                                      <Chip size="small" label={c.blocked_reason} color="warning"
                                        sx={{ height: 18, fontSize: '0.65rem' }} />
                                      {/* v11.0.0 — direct "Fix in Venues"
                                          link for cert-blocked rows. Audit:
                                          "These venues will fail silently
                                          (no KDM can be generated). This
                                          needs to be a hard blocker with a
                                          link to fix it." */}
                                      {c.blocked_reason === 'No cert on file' && (
                                        <Button
                                          size="small"
                                          variant="text"
                                          color="warning"
                                          onClick={() => window.open(`/venues?focus=${c.venue_id}`, '_blank')}
                                          sx={{ fontSize: '0.65rem', py: 0, minHeight: 18 }}
                                          data-testid="fix-cert-link"
                                        >
                                          Fix in Venues →
                                        </Button>
                                      )}
                                    </>
                                  ) : picked ? (
                                    <>
                                      <Chip
                                        size="small"
                                        label={recipChip}
                                        title={recipChip}
                                        sx={{
                                          height: 22,
                                          maxWidth: '100%',
                                          fontSize: '0.72rem',
                                          '& .MuiChip-label': { px: 1, overflow: 'hidden', textOverflow: 'ellipsis' },
                                        }}
                                      />
                                      <Tooltip title="Edit recipient / CC for this venue">
                                        <IconButton
                                          size="small"
                                          onClick={(e) => setEditPopover({ anchorEl: e.currentTarget, screen_id: c.screen_id })}
                                          sx={{ p: 0.5 }}
                                        >
                                          <EditIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  ) : (
                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.7rem' }}>
                                      {c.tech_contact_email || (deliveryMode === 'physical' ? 'no contact on file' : '—')}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </Box>
            )}
            {/* Edit popover for the picked-row recipient/CC. Anchored to
                the pencil icon. Submits state via updatePick — keeps the
                existing onSuccess + togglePick contract. */}
            <Popover
              open={Boolean(editPopover)}
              anchorEl={editPopover?.anchorEl}
              onClose={() => setEditPopover(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{ paper: { sx: { p: 2, minWidth: 360, maxWidth: 480 } } }}
            >
              {editPopover && picks[editPopover.screen_id] && (() => {
                const sid = editPopover.screen_id;
                const p = picks[sid];
                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography variant="subtitle2">Recipient for this venue</Typography>
                    {deliveryMode === 'digital' ? (
                      <>
                        <TextField size="small" label="To (primary)" fullWidth
                          value={p.recipient_email}
                          onChange={(e) => updatePick(sid, 'recipient_email', e.target.value)}
                          helperText="One email. The KDM goes to this address."
                        />
                        <TextField size="small" label="CC (others on this venue)" fullWidth multiline minRows={2}
                          value={p.cc_email}
                          onChange={(e) => updatePick(sid, 'cc_email', e.target.value)}
                          placeholder="comma-separated; leave blank to skip"
                          helperText="Auto-filled from the venue's role-routed contacts."
                        />
                      </>
                    ) : (
                      <TextField size="small" label="Contact name on the drive label" fullWidth
                        value={p.recipient_name}
                        onChange={(e) => updatePick(sid, 'recipient_name', e.target.value)}
                        helperText="Printed on the drive shipping label — venue's projectionist or operations contact."
                      />
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button size="small" onClick={() => setEditPopover(null)}>Done</Button>
                    </Box>
                  </Box>
                );
              })()}
            </Popover>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{results ? 'Close' : 'Cancel'}</Button>
        {!results && (
          <Button variant="contained" onClick={submit} disabled={busy || pickedCount === 0}>
            {busy ? <><CircularProgress size={16} sx={{ mr: 1 }} />Distributing…</> : `Distribute to ${pickedCount} ${pickedCount === 1 ? 'venue' : 'venues'}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
